import type { ReactNode } from 'react'
import { AlertTriangle, CheckCircle2, Clock, FlaskConical, PowerOff } from 'lucide-react'
import type { MailLogEntry, MailStatus } from '../../core/types'
import { useMailLog, useMailStatus } from './model'
import { Loading } from '../../ui/States'

/**
 * Email, for Norse Term admins: whether the email Apps Script is running,
 * whether it is still in test mode, and what it last sent. Read-only on
 * purpose. Turning email on, off or live happens in the script, signed in as
 * the sending account, so nobody can redirect email from the app.
 */

/** How long without a check before we say the script may have stopped (it runs every 5 minutes). */
const STALE_MS = 15 * 60 * 1000

export type MailHealth = 'unset' | 'off' | 'error' | 'stale' | 'test' | 'live'

const toDate = (v: unknown): Date | null =>
  v && typeof (v as { toDate?: unknown }).toDate === 'function' ? (v as { toDate: () => Date }).toDate()
    : typeof v === 'string' ? new Date(v) : v instanceof Date ? v : null

export function mailHealth(s: MailStatus | null, now: Date = new Date()): MailHealth {
  if (!s) return 'unset'
  if (s.on === false) return 'off'
  if (s.lastError) return 'error'
  const last = toDate(s.lastCheckAt) ?? toDate(s.updatedAt)
  if (!last || now.getTime() - last.getTime() > STALE_MS) return 'stale'
  return s.testTo ? 'test' : 'live'
}

const when = (v: unknown) => {
  const d = toDate(v)
  return d ? d.toLocaleString(undefined, { weekday: 'short', month: 'short', day: 'numeric', hour: 'numeric', minute: '2-digit' }) : ''
}

export function Email() {
  const status = useMailStatus(true)
  const log = useMailLog(true)
  if (status === undefined || log === null) return <Loading label="Loading email" />
  const health = mailHealth(status)
  const scriptUrl = status?.scriptId ? `https://script.google.com/home/projects/${status.scriptId}/edit` : null

  return (
    <>
      <StatusCard health={health} s={status} />

      <section className="card" style={{ marginTop: 'var(--s-3)' }}>
        <h2 className="ov__h" style={{ marginTop: 0 }}>What it sends</h2>
        <ul className="ov__p" style={{ paddingLeft: '1.2em', display: 'grid', gap: 'var(--s-1)' }}>
          <li><strong>A project proposal approved or sent back:</strong> the student gets an email within 5 minutes, from the reviewer
            (“Name via SailNorse”), with their note. Replies go to the reviewer.</li>
          <li><strong>Mondays at 7, the three weeks before the deadline and on the deadline itself:</strong> each seminar teacher gets every one of their
            students: course selections, and any project proposal with its title and where it stands. Anything still to do is
            highlighted.</li>
        </ul>
        <p className="opt__hint" style={{ margin: 0 }}>
          Subject lines never name a student. Turning email on, off or live is done in the script itself, signed in as
          {' '}{status?.owner || 'the sending account'}{scriptUrl ? <>: <a href={scriptUrl} target="_blank" rel="noreferrer">open the script</a></> : ''}.
        </p>
      </section>

      <section style={{ marginTop: 'var(--s-4)' }} aria-label="Recent email">
        <div className="steps__head">
          <h2 className="ov__h" style={{ margin: 0 }}>Recent email</h2>
          <span className="work__sub">{log.length ? `The last ${log.length}` : ''}</span>
        </div>
        {log.length === 0 ? <p className="muted-note">Nothing sent yet.</p> : (
          <>
            <table className="table">
              <thead><tr><th>When</th><th>Email</th><th>For</th><th>Result</th></tr></thead>
              <tbody>
                {log.map((m) => (
                  <tr key={m.id}>
                    <td>{when(m.sentAt ?? m.at)}</td>
                    <td>{what(m)}</td>
                    <td>{m.to}</td>
                    <td>{result(m)}</td>
                  </tr>
                ))}
              </tbody>
            </table>
            <ul className="work cards">
              {log.map((m) => (
                <li key={m.id} className="work__row" style={{ display: 'block' }}>
                  <div style={{ fontWeight: 610 }}>{what(m)}</div>
                  <div className="work__sub">{m.to} · {when(m.sentAt ?? m.at)}</div>
                  <div className="work__sub">{result(m)}</div>
                </li>
              ))}
            </ul>
          </>
        )}
      </section>
    </>
  )
}

const what = (m: MailLogEntry) => m.subject || (m.kind === 'seminar' ? 'Seminar report' : 'Project proposal decision')

function result(m: MailLogEntry): ReactNode {
  if (m.skipped) return 'Not sent: a later change replaced it'
  if (!m.sentAt) return 'Sending'
  if (m.test) return `Test mode: sent to ${m.sentTo}`
  return <><CheckCircle2 size={13} aria-hidden="true" /> Sent</>
}

function StatusCard({ health, s }: { health: MailHealth; s: MailStatus | null }) {
  const lastCheck = when(s?.lastCheckAt)
  const lastReport = when(s?.lastReportAt)
  const view: Record<MailHealth, { tone: string; icon: ReactNode; title: string; body: ReactNode }> = {
    unset: {
      tone: 'notice--info', icon: <PowerOff size={18} />, title: 'Not set up yet',
      body: 'No email is sent until the script is set up in the sending account. The steps are in docs/email.md.',
    },
    off: {
      tone: 'notice--warning', icon: <PowerOff size={18} />, title: 'Off',
      body: 'Email is turned off in the script. Run setUp there to turn it back on.',
    },
    error: {
      tone: 'notice--critical', icon: <AlertTriangle size={18} />, title: 'The last run failed',
      body: <>{s?.lastError}{s?.lastErrorAt ? <span className="work__sub"> ({when(s.lastErrorAt)})</span> : null}.
        {' '}It tries again every 5 minutes; nothing is lost while it fails.</>,
    },
    stale: {
      tone: 'notice--warning', icon: <Clock size={18} />, title: 'Not running',
      body: <>It checks every 5 minutes, but the last check was {lastCheck || 'never'}. The script may have been stopped, or the
        account that runs it may have lost access. Look at Executions in the script.</>,
    },
    test: {
      tone: 'notice--info', icon: <FlaskConical size={18} />, title: 'On, in test mode',
      body: <>Every email goes to <strong>{s?.testTo}</strong>, marked with who it was for. Students and teachers get nothing until
        goLive is run in the script.</>,
    },
    live: {
      tone: 'notice--info', icon: <CheckCircle2 size={18} />, title: 'On',
      body: 'Students and seminar teachers get their own email.',
    },
  }
  const v = view[health]
  return (
    <div className={`notice ${v.tone}`} style={{ marginTop: 'var(--s-2)' }}>
      <span aria-hidden="true">{v.icon}</span>
      <div>
        <strong>{v.title}</strong>
        <div>{v.body}</div>
        {s && (
          <div className="work__sub" style={{ marginTop: 4 }}>
            {s.owner ? <>Sends from {s.owner}. </> : null}
            {lastCheck ? <>Last check {lastCheck}. </> : null}
            {lastReport ? <>Last seminar reports {lastReport}{s.lastReportCount !== undefined ? ` (${s.lastReportCount})` : ''}.</> : null}
          </div>
        )}
      </div>
    </div>
  )
}
